import {
  generateColorMap,
  generateDivergingColors,
  generateSequentialColors
} from '@/components/Survey/Utilities/ColorUtility'
import { Question, Response } from '@/store/modules/survey-store-module'
import { isEndOption, sortRankingObject, sortSelectQuestionSeries } from '@/components/Survey/Utilities/ChartSortingUtilities'
import { capitalize } from 'lodash-es'
import Highcharts from 'highcharts'
import * as db from '@shared/types/database'
import store from '@/store'
import A from '@/utilities/alpha'
import { getOptionOrder, isOptionShuffled, QuestionOptions } from '@/components/Survey/Utilities/SurveyUtilities'
import { SelectOption } from '@alpha/ink/src/types/interfaces'
import { NotApplicableColor, SingleColor } from './StaticColors'
import { ScaleProperties } from '@shared/types/graphql'

export enum TextAreaChartType {
  Frequency = 'Word Cloud',
  Sentiment = 'Sentiment'
}

export enum RankChartType {
  Weighted = 'Average Rank',
  Distribution = 'Rank Distribution'
}

export type SliceOption = SelectOption & { value: Question['id'] | db.SliceType }
export type NestedResponses = Record<string, Response[]>
export type DoubleNestedResponses = Record<string, NestedResponses>
export type SegmentTotalsRecord = Record<string, Record<string, number>>

export interface SeriesData {
  name: string,
  y: number,
  color?: string,
}

export interface Series {
  data: number[] | SeriesData[]
  type: string
  name?: string
  visible?: boolean
  showInLegend: boolean
  dataLabels: {
    enabled: boolean,
    format?: string,
    inside?: boolean,
    crop?: boolean,
    allowOverlap?: boolean,
    overflow?: Highcharts.DataLabelsOverflowValue,
  }
  color?: string
}

export type ChartOptions = {
  title: {
    text: string
  },
  chart: {
    type: string,
    height: string
  },
  exporting: Highcharts.ExportingOptions,
  yAxis: {
    min: number,
    gridLineWidth: number,
    title: {},
    labels: {}
  },
  xAxis: {},
  tooltip: Highcharts.TooltipOptions,
  series?: Series[],
  credits: {
    enabled: boolean
  }
}

export const GLOBAL_CHART_HEIGHT = '500px'

/**
 * Generate the series objects for index (un-sliced) question data
 * @param data - The series data to put in the expected highcharts format
 */
export const generateIndexSeries = (data: SeriesData[]): Series[] => {
  return [
    {
      type: 'column',
      visible: true,
      dataLabels: {
        enabled: true,
        crop: false,
        overflow: 'allow',
        allowOverlap: true,
        inside: false,
        format: '{y}%'
      },
      data,
      showInLegend: false
    }
  ]
}

/**
 * Generate the series objects for sliced data
 * @param segmentTotals The totals for each segment of the slice
 * @param categories The list of choices for a question
 * @param colorOptions The colors being used for the series data
 */
export const generateSlicedSeries = (
  segmentTotals: SegmentTotalsRecord,
  colorOptions: string[],
  categories: string[],
  options?: { hideLabels?: boolean }
) => {
  const sortedTotals = optionTotalsBySegment(segmentTotals, categories)

  const colorMap = generateColorMap(categories, colorOptions)

  return generateSeriesObjects(sortedTotals, colorMap, '%', options?.hideLabels)
}

export const generateSlicedRankSeries = (responses: NestedResponses, colorOptions: string[], categories: string[]): Series[] => {
  // First get the weighted averages for all the choices.
  // weightsBySegment = { "Male" : { "Ranch": 2.25, "Honey BBQ": 2.29 }, "Female": { "Ranch": 2.42, "Honey BBQ": 2.68 }}
  const weightsBySegment: Record<string, Record<string, number>> = {}
  Object.entries(responses).forEach(([segmentKey, totals]) => {
    const segmentTotals = getRankingTotals(categories, totals.map(response => response.answer))
    weightsBySegment[segmentKey] = calcWeightedAverage(segmentTotals)
  })

  // Now get the weights into buckets according to the choices.
  // weightsByOption will look like { "Ranch": [2.25, 2.42], "Honey BBQ": [2.29, 2.68] }
  const weightsByOption : Record<string, number[]> = {}
  for (const totalsBySegment of Object.values(weightsBySegment)) {
    // totalsBySegment = "Male": { "Ranch" : 2.25, "Honey BBQ" : 2.42 }
    for (const optionId of categories) {
      if (optionId === 'segmentCount') continue
      // optionId = "Ranch"
      weightsByOption[optionId] ? weightsByOption[optionId].push(totalsBySegment[optionId])
        : weightsByOption[optionId] = [totalsBySegment[optionId]]
    }
  }

  // Generate series objects which highcharts expects
  const colorMap = generateColorMap(categories, colorOptions)
  return generateSeriesObjects(weightsByOption, colorMap, '')
}

/**
 * Generate the series objects for matrix question index and sliced data
 * @param matrixRankings - The rankings being put into series objects. Comes from getMatrixTotals
 * @param categories - Scale categories for matrix category (1-5 for example)
 * @param displayCategories - Scale labels with extremity labels added
 * @param options - Used to pass diff config options
 * @optional `hasNaOpt` Whether or not question has NA answer option, defaults to false
 * @optional `hideLabels` Hide chart labels, defaults to false
 * @optional `colors` Inject custom chart colors, defaults to feedback loops palette
 */
export const generateMatrixSeries = (
  matrixRankings: SegmentTotalsRecord,
  categories: string[],
  displayCategories: string[],
  options: { hideLabels?: boolean, hasNaOpt: boolean, colors?: string[] }
) => {
  const { hideLabels, hasNaOpt, colors } = options
  if (colors?.length !== 3) console.warn('Matrix series requires exactly 3 colors (divergent palette)')
  const scaleCategories = hasNaOpt ? [...categories, QuestionOptions.NA] : [...categories]
  const sortedTotals = optionTotalsBySegment(matrixRankings, scaleCategories)

  const divergingLength = hasNaOpt ? Object.keys(sortedTotals).length - 1 : Object.keys(sortedTotals).length
  const colorPalette = generateDivergingColors(divergingLength, colors).reverse()
  if (hasNaOpt) {
    colorPalette.push(NotApplicableColor)
  }
  const colorMap = generateColorMap(scaleCategories, colorPalette)

  const matrixSeriesData = generateSeriesObjects(sortedTotals, colorMap, '%', hideLabels)
  const sortedSeriesData = []

  if (hasNaOpt) {
    const naData = matrixSeriesData.find(o => o.name === QuestionOptions.NA)
    if (naData) {
      sortedSeriesData.push(naData)
    }
  }

  matrixSeriesData.forEach((dataSet, index) => {
    if (dataSet.name !== QuestionOptions.NA) {
      dataSet.name = displayCategories[index]
      sortedSeriesData.push(dataSet)
    }
  })

  return sortedSeriesData
}

/**
 * Generate the final series objects that are used in highcharts
 * @param sortedTotals - The totals that will be used as the data for the chart
 * @param colorOptions - The colors to be used for the chart
 * @param dataLabelSuffix - What suffix to add to data labels. Defaults to %
 * @param hideLabels - Show or hide data labels. Defaults to false
 */
const generateSeriesObjects = (
  sortedTotals: Record<string, number[]>,
  colorOptions: Record<string, string>,
  dataLabelSuffix = '%',
  hideLabels = false
): Series[] => {
  // Put the data in the expected highCharts series objects
  return Object.entries(sortedTotals).map(([key, segmentAnswers]) => {
    return {
      dataLabels: {
        enabled: hideLabels ? false : enableSlicedColumnLabels(Object.keys(sortedTotals).length, segmentAnswers.length),
        format: `{y}${dataLabelSuffix}`,
        overflow: 'allow',
        inside: false,
        crop: false,
        allowOverlap: true
      },
      type: 'column',
      showInLegend: !hideLabels,
      name: key,
      data: segmentAnswers,
      color: colorOptions[key]
    }
  })
}

/**
 * Get a sum of the different options categorized by segments (Variant, Demographic, etc)
 * @return Record<string, Record<string, number>> :
 * { "1": { "Option 1" : 23, "Option 2" : 100, "segmentCount" : 123 },
 *   "2": { "Option 1" : 29, "Option 2" : 100, "segmentCount" : 129 } }
 */
export const getChoiceTotalsBySegment = (responses: NestedResponses) => {
  const segmentTotals: SegmentTotalsRecord = {}
  for (const [key, segmentResponses] of Object.entries(responses)) {
    const answers = segmentResponses.map(response => response.answer).flat()
    segmentTotals[key] = answers.reduce((totals: Record<string, number>, currentAnswer) => {
      totals[currentAnswer] = !totals[currentAnswer] ? 1 : totals[currentAnswer] + 1
      totals.segmentCount ? totals.segmentCount += 1 : totals.segmentCount = 1

      if (Array.isArray(segmentResponses[0].answer)) {
        totals.segmentCount = segmentResponses.length
      }

      return totals
    }, {})
  }
  return segmentTotals
}

/**
 * Get a sum of the different sentiments categorized by segments (Variant, Demographic, etc)
 * @return Record<string, Record<string, number>> :
 * { "1": { "Negative" : 23, "Neutral" : 100, "Positive" : 2, "segmentCount" : 125 },
 *   "2": { "Negative" : 29, "Neutral" : 100, "Positive" : 5, "segmentCount" : 134 } }
 */
export const getSentimentTotalsBySegment = (responses: NestedResponses) => {
  const segmentTotals: SegmentTotalsRecord = {}
  for (const [key, segmentResponses] of Object.entries(responses)) {
    const sentiment = segmentResponses.map(response => capitalize(response.sentimentLabel!)).flat()
    segmentTotals[key] = sentiment.reduce((totals: Record<string, number>, sentimentLabel) => {
      const capSentimentLabel = capitalize(sentimentLabel)
      totals[capSentimentLabel] = !totals[capSentimentLabel] ? 1 : totals[capSentimentLabel] + 1
      totals.segmentCount ? totals.segmentCount += 1 : totals.segmentCount = 1
      return totals
    }, {})
  }
  return segmentTotals
}

/**
 * Get the sorted ranking totals for a Rank question
 * @param categories The choices being ranked
 * @param indexResponses The responses (rankings) for the rank question
 * @return Ends up looking something like this
 *           { "Ranch": { "1" : 23, "2" : 100, "3" : 12, "4" : 32, "5" : 12, "segmentCount": 179 },
 *            "BBQ":   { "1" : 99, "2" : 12, "3" : 33, "4" : 55, "5" : 12, "segmentCount": 179 } }
 */
export const getRankingTotals = (categories: string[], indexResponses: string[][]) => {
  const rankingTotalsByResponses : SegmentTotalsRecord = generateRankingsRecords(categories, categories.length)

  for (const rankings of indexResponses) {
    // choice = BBQ, Honey, Ranch, etc ... what they ranked, and the position of it is the "Ranking"
    rankings.forEach((choice, index) => {
      const ranking = (index + 1).toString()
      rankingTotalsByResponses[choice][ranking] += 1
      // Keep track of the overall count of the ranking for ease of use later on
      rankingTotalsByResponses[choice].segmentCount += 1
    })
  }
  // Final object looks something like this
  // { "Ranch": { "1" : 23, "2" : 100, "3" : 12, "4" : 32, "5" : 12, "segmentCount": 179 },
  //   "BBQ":   { "1" : 99, "2" : 12, "3" : 33, "4" : 55, "5" : 12, "segmentCount": 179 } }

  // Want the first ranked object to be first, so use sort key 1
  return sortRankingObject(rankingTotalsByResponses, 1, true)
}

/**
 * Get the sorted ranking totals for a Matrix question
 * @param categories The choices being ranked
 * @param sortKey The max scale rating of the matrix
 * @param scaleSize How big the rating scale is for the matrix question
 * @param indexResponses The responses (ranking for each choice) of the matrix question
 * @param hasNaOpt Whether or not the question has NA opt
 * @return Ends up looking something like this
 *           { "Ranch": { "1" : 23, "2" : 100, "3" : 12, "4" : 32, "5" : 12, "segmentCount": 179 },
 *            "BBQ":   { "1" : 99, "2" : 12, "3" : 33, "4" : 55, "5" : 12, "segmentCount": 179 } }
 */
export const getMatrixTotals = (
  categories: { text: string, hash?: string, order?: number }[],
  sortKey: number, scaleSize: number,
  indexResponses: Record<string, number | string>[],
  hasNaOpt: boolean) => {
  const matrixRatings = normalizeMatrixData(indexResponses, categories)
  const textCategories = categories.map((category) => category.text)
  const rankingTotalsByResponses: SegmentTotalsRecord = generateRankingsRecords(textCategories, scaleSize, hasNaOpt)
  for (const rankings of matrixRatings) {
    // The list 'rankings' is what they ranked the choices. 4, 3, 4, 5, etc ...
    // The position in 'rankings' list references is used to index the category they were ranking. categories[index] = BBQ or Honey or Ranch... etc
    rankings.forEach((ranking, index) => {
      const choice = textCategories[index]

      // TODO: This is temporary as this is how NA choices come through (null)
      // We want to fix this in the future to be a consistent string coming in from Tide to Channel to Reef
      if (ranking === null || ranking === 'not applicable') {
        rankingTotalsByResponses[choice][QuestionOptions.NA] += 1
      } else {
        rankingTotalsByResponses[choice][ranking] += 1
      }

      rankingTotalsByResponses[choice].segmentCount += 1
    })
  }

  // Add order for locked options
  categories.forEach((category) => {
    if (category.order !== undefined) {
      rankingTotalsByResponses[category.text].order = category.order
    }
  })
  return sortRankingObject(rankingTotalsByResponses, sortKey, true)
}

/**
 * Initializes the records for the rank/matrix questions so that if a choice is not selected, it is still represented as 0
 * @param categories - List of question options
 * @param scaleSize - How big the rating/ranking scale is
 * @param hasNaOpt - Whether or not the question has Not Applicable option
 */
const generateRankingsRecords = (categories: string[], scaleSize: number, hasNaOpt = false) => {
  const rankingTotalsByResponses: SegmentTotalsRecord = {}

  categories.forEach(category => {
    rankingTotalsByResponses[category] = {}
    if (hasNaOpt) {
      rankingTotalsByResponses[category][QuestionOptions.NA] = 0
    }
    for (let i = 1; i <= scaleSize; i++) {
      rankingTotalsByResponses[category][i] = 0
    }

    rankingTotalsByResponses[category].segmentCount = 0
  })

  return rankingTotalsByResponses
}

/**
 * Add the respondent count of each segment to the categories for display purposes.
 * "BBQ
 *  n=177"
 * @param segmentTotals: The totals for options separated by demographic
 */
export const getCategoriesForSlicedQuestion = (segmentTotals: SegmentTotalsRecord, questionSlicedBy?: Question) => {
  let labels = Object.keys(segmentTotals)
  const totals = Object.values(segmentTotals)

  if (questionSlicedBy && (questionSlicedBy.type === db.QuestionType.Rate || questionSlicedBy.type === db.QuestionType.RateOnly)) {
    labels = getCategoriesWithExtremityLabels(labels, questionSlicedBy.scaleProperties!)
  }

  labels = labels.map((label, idx) => {
    const count = totals[idx].segmentCount || 0
    return label + ` <br/>N=${count}`
  })

  return labels
}

/**
 * Add the respondent count of each segment to the categories for display purposes.
 * "BBQ
 *  n=177"
 * @param responses the responses for the question
 * @param categories used to get totals
 */
export const getCategoriesForSlicedRankQuestion = (responses: NestedResponses, categories: string[], questionSlicedBy?: Question) => {
  const sortedTotals: Record<string, Record<string, Record<string, number>>> = {}
  Object.entries(responses).forEach(([segmentKey, totals]) => {
    sortedTotals[segmentKey] = getRankingTotals(categories, totals.map(response => response.answer))
  })

  let labels = Object.keys(sortedTotals)
  const totals = Object.values(sortedTotals)

  if (questionSlicedBy && (questionSlicedBy.type === db.QuestionType.Rate || questionSlicedBy.type === db.QuestionType.RateOnly)) {
    labels = getCategoriesWithExtremityLabels(labels, questionSlicedBy.scaleProperties!)
  }

  labels = labels.map((label, index) => {
    const totalsBySegment = totals[index]
    const choiceKeys = Object.keys(totalsBySegment)
    return `${label} <br/>N=${totalsBySegment[choiceKeys[0]].segmentCount}`
  })

  return labels
}

/**
 * Get the axis labels and add proper n sizes to them for variant questions
 * @param responses - The responses for the question
 *        Data looks like { "Male": { "Variant 1": Response[], "Variant 2": Response[] }
 * @param selectedTab - Currently selected demographic or variant ... this is used to get proper n sizes
 * @return A list that looks like ["Variant 1", "Variant 2"]
 */
export const getCategoriesForSlicedVariantQuestion = (responses: DoubleNestedResponses, selectedTab: string) => {
  return Object.entries(responses[selectedTab]).map(([key, responses]) => {
    return `${key} <br/>N=${responses.length}`
  })
}

export const getCategoriesWithExtremityLabels = (categories: string[], scaleProperties: ScaleProperties) => {
  return categories.map((category, index) => {
    if (index === 0) {
      return category + ' ' + scaleProperties!.minLabel!
    } else if (index === categories.length - 1) {
      return category + ' ' + scaleProperties!.maxLabel!
    }
    return category
  })
}

/**
 * Get a total for each option in a question
 * @param responses The responses being summed
 * @return A record keyed by the question options with value of the totals
 */
export const getResponseTotalsForOptions = (responses: Response[]) => {
  return responses.reduce((totals: Record<string, number>, response: Response) => {
    const { answer } = response
    if (Array.isArray(answer)) {
      // Multi select is a special case as they can have multiple choices (lol). Iterate to add each of the choices.
      answer.forEach(multiSelectAnswer => {
        totals[multiSelectAnswer] = !totals[multiSelectAnswer] ? 1 : totals[multiSelectAnswer] + 1
      })
    } else {
      totals[answer] = !totals[answer] ? 1 : totals[answer] + 1
    }
    return totals
  }, {})
}

/**
 * Get total responses for each sentiment bucket
 * @param responses The responses being summed
 * @return A record keyed by the sentiment labels with value of the totals
 */
export const getResponseSentimentTotals = (responses: Response[]) => {
  return responses.reduce((totals: Record<string, number>, response: Response) => {
    const capSentimentLabel = capitalize(response.sentimentLabel)
    totals[capSentimentLabel!] = !totals[capSentimentLabel!] ? 1 : totals[capSentimentLabel!] + 1
    return totals
  }, {})
}

/**
 * Calculates percentage as whole number
 * @param numerator What you are dividing!
 * @param denominator What you are dividing by!
 */
export const calcPercentage = (numerator: number, denominator: number) => {
  if (!numerator || !denominator) return 0
  return Number(((numerator / denominator) * 100).toFixed(0))
}

/**
 * TODO
 * @param rankings
 */
export const calcWeightedAverage = (rankings: SegmentTotalsRecord) => {
  const weightedRankings: Record<string, number> = {}

  Object.entries(rankings).forEach(([category, totals]) => {
    let maxWeight = Object.keys(rankings).length - 1
    let weightedTotal = 0
    Object.entries(totals).forEach(([ranking, total]) => {
      if (ranking === 'segmentCount') return
      weightedTotal += (total * maxWeight--)
    })
    weightedRankings[category] = Number((weightedTotal / rankings[category].segmentCount).toFixed(2))
  })

  return weightedRankings
}

/**
 * Used to determine the count of responses for a demographic/variant segment
 * @param segmentResponses The variant responses for the selected segment
 */
export const getSegmentCount = (segmentResponses: NestedResponses | Response[]): number => {
  if (Array.isArray(segmentResponses)) return segmentResponses.length
  let length = 0
  Object.values(segmentResponses).forEach(r => {
    length += r.length
  })
  return length
}

/**
 * Round numeric answers and convert them to whole numbers
 * @param responses The responses which contain the answers being normalized
 */
export const normalizeData = (responses: Response[]) => {
  return responses.map(response => {
    return {
      ...response,
      answer: Math.round(Number(response.answer)).toString()
    }
  })
}

// TODO: Comment
const normalizeMatrixData = (indexResponses: Record<string, number | string>[], categories: {text: string, hash?: string}[]) => {
  // need sorted categories with hash values
  const matrixRatings = []
  for (const responses of indexResponses) {
    const responseRatings: Array<string | number> = []
    Object.keys(responses).forEach(key => {
      const categoryIndex = categories.findIndex((category) => category.hash === key)
      responseRatings[categoryIndex] = responses[key]
    })
    matrixRatings.push(responseRatings)
  }

  return matrixRatings
}

/**
 * Find the option inside of the matching variant parent question by using qualifying tags.
 * Return the option label if found.
 * @param parentQuestion The parent question being searched
 * @param qualifyingTag The tag of child question being searched for
 */
export const getVariantParentOptionText = (parentQuestion: Question | undefined, qualifyingTag: string) => {
  if (!parentQuestion) return ''

  // Assuming for now that questions only have one qualifying tag
  const option = parentQuestion.options!.find(option => {
    if (option?.qualificationTags) {
      return option.qualificationTags[0] === qualifyingTag
    }
    return false
  })

  return option?.label ? `<b>"${option.label}"</b> ` : ''
}

/**
 * Determines if the percentage labels displayed above columns should be shown on sliced charts
 * @param barCount - How many bars each category will have
 * @param categoryCount - How many categories the chart will have
 */
const enableSlicedColumnLabels = (barCount: number, categoryCount: number) => {
  if (categoryCount < 5) {
    return true
  }
  return barCount < 6 && categoryCount < 10
}

/** Get each segment's total for each option into lists keyed by the option names
 * @param segmentTotals - Optional totals categorized by segments (example input comes from getChoiceTotalsBySegment)
 * { "Variant 1": { "Option 1" : 23, "Option 2" : 100 },
 *   "Variant 2": { "Option 1" : 23, "Option 2" : 100 } }
 * @param categories The categories of the question
 * @return { "Option 1" : [21, 22, 190] }
 * Where the values in the list are totals for the option by segment. This is how highCharts expects the data.
 */
const optionTotalsBySegment = (segmentTotals: SegmentTotalsRecord, categories: string[]) => {
  const sortedTotals: Record<string, number[]> = {}
  for (const totalsBySegment of Object.values(segmentTotals)) {
    // totalsBySegment = { "Option 1" : 23, "Option 2" : 100 }
    for (const optionId of categories) {
      let percent = 0
      if (totalsBySegment[optionId]) {
        percent = calcPercentage(totalsBySegment[optionId], totalsBySegment.segmentCount)
      }
      // optionId = "Option 1"
      sortedTotals[optionId] ? sortedTotals[optionId].push(percent)
        : sortedTotals[optionId] = [percent]
    }
  }
  return sortedTotals
}

export const generateChartSubtitle = (segmentRespondentCount: number, selectedSlice: string, selectedTab?: string) => {
  if (!Object.values(db.SliceType).includes(selectedSlice as db.SliceType)) {
    selectedSlice = getSliceByQuestionDisplayTitle(selectedSlice)
  }

  let sliceInfo: string
  if (selectedTab && selectedSlice === db.SliceType.DEFAULT) {
    // Case for charts that can't be sliced
    sliceInfo = `By ${selectedTab}`
  } else if (selectedTab) {
    sliceInfo = `${selectedSlice}: ${selectedTab}`
  } else {
    sliceInfo = `By ${selectedSlice}`
  }

  return `N=${segmentRespondentCount} | ${sliceInfo}`
}

export const generateSentimentSubtitle = (segmentRespondentCount: number, selectedSlice: string, selectedTab?: string) => {
  if (!Object.values(db.SliceType).includes(selectedSlice as db.SliceType)) {
    selectedSlice = getSliceByQuestionDisplayTitle(selectedSlice)
  }

  const slicedSegmentedInfo = selectedSlice === db.SliceType.DEFAULT
    ? 'Sentiment'
    : `Sentiment and ${selectedSlice}`

  const sliceInfo = selectedTab
    ? `By ${slicedSegmentedInfo}: ${selectedTab}`
    : `By ${slicedSegmentedInfo}`

  return `N=${segmentRespondentCount} | ${sliceInfo}`
}

const getSliceByQuestionDisplayTitle = (selectedSlice: string) => {
  const sliceByQuestion = store.getters.survey.questionById(selectedSlice)

  return A.stripHTML(`${store.getters.surveyConfiguration.questionConfigById(selectedSlice).title || sliceByQuestion?.text || ''}`)
}

/** Sort single select series data and add colors
 */
export const sortSingleSelectSeriesData = (indexSeriesData: SeriesData[], questionOptions: { text: string, order: number, shuffle: boolean }[], color?: string) => {
  const seriesData: SeriesData[] = []
  const lockedSeriesData: { order: number, data: SeriesData }[] = []
  const beginningOptions: SeriesData[] = []
  const endOptions: SeriesData[] = []

  indexSeriesData.forEach((totals) => {
    const data: SeriesData = {
      ...totals,
      color: color || SingleColor
    }

    if (['Yes', 'No'].includes(totals.name)) {
      beginningOptions.push(data)
    } else if (isEndOption(totals.name)) {
      // End options always go at the end regardless of sorting
      endOptions.push(data)
    } else if (isOptionShuffled(totals.name, questionOptions)) {
      // Shuffled options can be sorted
      seriesData.push(data)
    } else {
      // Non shuffled options are "locked" by the test creators and should remain in the original order
      const order = getOptionOrder(totals.name, questionOptions)
      lockedSeriesData.push({ order, data })
    }
  })

  return sortSelectQuestionSeries(seriesData, lockedSeriesData, beginningOptions, endOptions)
}

export const generateRankIndexSeriesData = (weightedRankings: Record<string, number>, color?: string) => {
  const colors = generateSequentialColors(Object.keys(weightedRankings).length, color)

  const seriesData: SeriesData[] = Object.keys(weightedRankings).map(category => {
    // Generate initial objects
    return {
      name: category,
      y: weightedRankings[category]
    }
  }).sort((a, b) => {
    // Sort greatest to least
    return b.y - a.y
  }).map((category, index) => {
    // Add the colors now that we know the sorting
    return {
      ...category,
      color: colors[index]
    }
  })
  return seriesData
}

export const generateNumberIndexSeriesData = (categories: string[], indexTotals: Record<string, number>, responseCount: number, colors: string[]) => {
  const data = categories.map((category, index: number) => {
    return {
      name: category,
      y: calcPercentage(indexTotals[category], responseCount),
      color: colors[index]
    }
  })
  return data
}
